import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight, CalendarDays, Film, HardDrive, Images, LayoutGrid, Layers, Megaphone, Music4, Search, Settings, Tv,
} from 'lucide-react'
import { programsApi, cgTemplatesApi, gridTemplatesApi } from '../../lib/api.js'
import { campaignsApi } from '../../lib/adsApi.js'
import { PROGRAM_TYPE_LABELS, ANTENNA_ELEMENT_PROGRAM_TYPES, formatDurationShort } from '../../lib/utils.js'
import type { Program, ProgramType } from '../../types/index.js'
import './CommandPalette.css'

export type PaletteView = 'agenda' | 'templates' | 'programs' | 'jingles' | 'media' | 'cg' | 'ads' | 'infoservice' | 'reports' | 'settings'

/** Cible d'une entrée : une vue, éventuellement centrée sur un élément. */
export interface PaletteTarget {
  view:      PaletteView
  programId?: string
}

interface PaletteEntry {
  id:      string
  group:   string
  label:   string
  detail?: string
  icon:    React.ReactNode
  target:  PaletteTarget
}

const NAVIGATION: PaletteEntry[] = [
  { id: 'nav:agenda',    group: 'Aller à', label: 'Grille',            icon: <CalendarDays size={15} />, target: { view: 'agenda' } },
  { id: 'nav:templates', group: 'Aller à', label: 'Modèles de grille', icon: <LayoutGrid size={15} />,   target: { view: 'templates' } },
  { id: 'nav:cg',        group: 'Aller à', label: 'Habillage CG',      icon: <Layers size={15} />,       target: { view: 'cg' } },
  { id: 'nav:programs',  group: 'Aller à', label: 'Programmes',        icon: <Film size={15} />,         target: { view: 'programs' } },
  { id: 'nav:jingles',   group: 'Aller à', label: 'Éléments d’antenne', icon: <Music4 size={15} />,       target: { view: 'jingles' } },
  { id: 'nav:media',     group: 'Aller à', label: 'Médiathèque',       icon: <HardDrive size={15} />,    target: { view: 'media' } },
  { id: 'nav:ads',       group: 'Aller à', label: 'Régie pub',         icon: <Megaphone size={15} />,    target: { view: 'ads' } },
  { id: 'nav:infoservice', group: 'Aller à', label: 'Infoservice',     detail: 'Annonces et diffusion', icon: <Images size={15} />, target: { view: 'infoservice' } },
  { id: 'nav:reports',   group: 'Aller à', label: 'Rapports & AsRun',  icon: <Tv size={15} />,           target: { view: 'reports' } },
  { id: 'nav:settings',  group: 'Aller à', label: 'Réglages',          detail: 'Diagnostic playout, chaînes', icon: <Settings size={15} />, target: { view: 'settings' } },
]

function matches(haystack: string, needle: string) {
  return haystack.toLocaleLowerCase('fr').includes(needle)
}

function programEntry(program: Program): PaletteEntry {
  const isJingle = ANTENNA_ELEMENT_PROGRAM_TYPES.includes(program.type)
  return {
    id:     `program:${program.id}`,
    group:  isJingle ? 'Éléments d’antenne' : 'Programmes',
    label:  program.title,
    detail: `${PROGRAM_TYPE_LABELS[program.type as ProgramType] ?? program.type} · ${formatDurationShort(program.durationSec)}`,
    icon:   isJingle ? <Music4 size={15} /> : <Film size={15} />,
    target: { view: isJingle ? 'jingles' : 'programs', programId: program.id },
  }
}

interface CommandPaletteProps {
  channelId: string | null
  onClose:   () => void
  onNavigate: (target: PaletteTarget) => void
}

export const CommandPalette = memo(function CommandPalette({ channelId, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState<PaletteEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [cursor, setCursor]   = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef  = useRef<HTMLDivElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  // Recherche : le catalogue est interrogé côté serveur (paramètre `search`),
  // les campagnes et les modèles sont filtrés côté client — leurs listes sont
  // courtes et déjà chargées en une requête.
  useEffect(() => {
    const needle = query.trim().toLocaleLowerCase('fr')
    if (needle.length < 2 || !channelId) {
      setResults([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    const timer = setTimeout(async () => {
      const [programs, campaigns, cgTemplates, gridTemplates] = await Promise.all([
        programsApi.list({ channelId, search: needle, limit: 8, sortBy: 'title', sortDir: 'asc' })
          .then(res => (res?.data ?? []) as Program[]).catch(() => [] as Program[]),
        campaignsApi.list({ channelId }).catch(() => [] as any[]),
        cgTemplatesApi.list(channelId).catch(() => [] as any[]),
        gridTemplatesApi.list(channelId).catch(() => [] as any[]),
      ])
      if (cancelled) return

      const entries: PaletteEntry[] = [
        ...programs.map(programEntry),
        ...campaigns
          .filter((campaign: any) => matches(String(campaign?.name ?? ''), needle))
          .slice(0, 5)
          .map((campaign: any): PaletteEntry => ({
            id:     `campaign:${campaign.id}`,
            group:  'Campagnes',
            label:  campaign.name,
            detail: campaign.advertiser?.name ?? undefined,
            icon:   <Megaphone size={15} />,
            target: { view: 'ads' },
          })),
        ...cgTemplates
          .filter((template: any) => matches(String(template?.name ?? ''), needle))
          .slice(0, 5)
          .map((template: any): PaletteEntry => ({
            id:     `cg:${template.id}`,
            group:  'Habillages',
            label:  template.name,
            detail: template.htmlPath ?? undefined,
            icon:   <Layers size={15} />,
            target: { view: 'cg' },
          })),
        ...gridTemplates
          .filter((template: any) => matches(String(template?.name ?? ''), needle))
          .slice(0, 5)
          .map((template: any): PaletteEntry => ({
            id:     `grid-template:${template.id}`,
            group:  'Modèles de grille',
            label:  template.name,
            icon:   <LayoutGrid size={15} />,
            target: { view: 'templates' },
          })),
      ]

      setResults(entries)
      setLoading(false)
    }, 180)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [query, channelId])

  const entries = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('fr')
    const navigation = needle
      ? NAVIGATION.filter(entry => matches(entry.label, needle))
      : NAVIGATION
    return [...results, ...navigation]
  }, [results, query])

  useEffect(() => { setCursor(0) }, [entries.length])

  const activate = useCallback((entry: PaletteEntry) => {
    onNavigate(entry.target)
    onClose()
  }, [onNavigate, onClose])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor(current => (entries.length ? (current + 1) % entries.length : 0))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor(current => (entries.length ? (current - 1 + entries.length) % entries.length : 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const entry = entries[cursor]
      if (entry) activate(entry)
    }
  }, [activate, cursor, entries, onClose])

  // Garde l'élément sélectionné au clavier dans le champ visible.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-cursor="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  const handleOverlayMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose()
  }, [onClose])

  let renderedGroup: string | null = null

  return (
    <div className="tp-palette-overlay" onMouseDown={handleOverlayMouseDown} onKeyDown={handleKeyDown}>
      <section className="tp-palette" role="dialog" aria-modal="true" aria-label="Recherche globale">
        <div className="tp-palette__field">
          <Search size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="tp-palette__input"
            placeholder="Programme, média, spot, template…"
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-label="Rechercher"
            autoComplete="off"
          />
          <kbd className="tp-palette__kbd">Échap</kbd>
        </div>

        <div className="tp-palette__results" ref={listRef} role="listbox" aria-label="Résultats">
          {loading && <div className="tp-palette__status">Recherche…</div>}

          {!loading && entries.length === 0 && (
            <div className="tp-palette__status">Aucun résultat</div>
          )}

          {entries.map((entry, index) => {
            const showGroup = entry.group !== renderedGroup
            renderedGroup = entry.group
            return (
              <div key={entry.id}>
                {showGroup && <div className="tp-palette__group">{entry.group}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === cursor}
                  data-cursor={index === cursor ? 'true' : undefined}
                  className={`tp-palette__entry${index === cursor ? ' is-cursor' : ''}`}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => activate(entry)}
                >
                  <span className="tp-palette__entry-icon" aria-hidden="true">{entry.icon}</span>
                  <span className="tp-palette__entry-label">{entry.label}</span>
                  {entry.detail && <span className="tp-palette__entry-detail">{entry.detail}</span>}
                  <ArrowRight size={13} className="tp-palette__entry-go" aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>

        <footer className="tp-palette__footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
          <span><kbd>↵</kbd> ouvrir</span>
          <span><kbd>Échap</kbd> fermer</span>
        </footer>
      </section>
    </div>
  )
})
